/**
 * ストレスカード
 *
 * 平均ストレス、最大ストレス、ストレス分布を表示
 */

import { Brain } from 'lucide-react'

import type { GarminStressSummary } from '@/api/types/garmin'
import { formatSecondsShort } from '@/lib/time-format'
import { cn } from '@/lib/utils'

type Props = {
  stress: GarminStressSummary | null
  className?: string
}

const getStressLevel = (avg: number | null | undefined): { label: string; color: string; bgColor: string } => {
  if (avg === null || avg === undefined) return { label: '--', color: 'text-muted-foreground', bgColor: 'bg-muted' }
  if (avg < 26) return { label: 'リラックス', color: 'text-blue-600', bgColor: 'bg-blue-500' }
  if (avg < 51) return { label: '低', color: 'text-green-600', bgColor: 'bg-green-500' }
  if (avg < 76) return { label: '中', color: 'text-yellow-600', bgColor: 'bg-yellow-500' }
  return { label: '高', color: 'text-red-600', bgColor: 'bg-red-500' }
}

export const StressCard = ({ stress, className }: Props) => {
  const level = getStressLevel(stress?.avg)
  const avgValue = stress?.avg ?? 0

  // ストレス分布データ
  const distributionData = [
    { label: '休息', seconds: stress?.rest_stress_duration, color: 'bg-blue-500' },
    { label: '活動', seconds: stress?.activity_stress_duration, color: 'bg-yellow-500' },
    { label: '高', seconds: stress?.high_stress_duration, color: 'bg-red-500' },
  ]

  const totalDuration = distributionData.reduce((sum, d) => sum + (d.seconds ?? 0), 0)

  return (
    <div className={cn('rounded-xl border bg-card p-4 shadow-sm', className)}>
      <div className="flex items-center gap-2 mb-3">
        <Brain className="size-5 text-purple-500" />
        <span className="text-sm font-medium">ストレス</span>
      </div>

      {/* 平均ストレス */}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1">
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold tabular-nums">{stress?.avg ?? '--'}</span>
            <span className={cn('text-sm font-medium', level.color)}>{level.label}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">最大: {stress?.max ?? '--'}</p>
        </div>

        {/* ストレスメーター */}
        <div className="relative size-16">
          <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
            <circle cx="18" cy="18" r="15.9155" fill="none" stroke="var(--muted)" strokeWidth="3" />
            <circle
              cx="18"
              cy="18"
              r="15.9155"
              fill="none"
              stroke={level.bgColor
                .replace('bg-', 'rgb(var(--')
                .replace('-500', '))')
                .replace('rgb(var(--blue))', '#3b82f6')
                .replace('rgb(var(--green))', '#22c55e')
                .replace('rgb(var(--yellow))', '#eab308')
                .replace('rgb(var(--red))', '#ef4444')
                .replace('rgb(var(--muted))', 'var(--muted)')}
              strokeWidth="3"
              strokeDasharray={`${avgValue}, 100`}
              strokeLinecap="round"
              className="transition-all duration-500"
            />
          </svg>
        </div>
      </div>

      {/* ストレス分布バー */}
      {totalDuration > 0 && (
        <>
          <div className="h-2 flex rounded-full overflow-hidden mb-2">
            {distributionData.map((d, i) => (
              <div
                key={i}
                className={cn('transition-all', d.color)}
                style={{ width: `${((d.seconds ?? 0) / totalDuration) * 100}%` }}
              />
            ))}
          </div>

          {/* 分布詳細 */}
          <div className="grid grid-cols-3 gap-1 text-xs">
            {distributionData.map((d, i) => (
              <div key={i} className="flex flex-col items-center p-1">
                <div className={cn('w-2 h-2 rounded-full mb-1', d.color)} />
                <span className="text-muted-foreground">{d.label}</span>
                <span className="font-medium tabular-nums">{formatSecondsShort(d.seconds ?? null)}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
